import { useState, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { TextField } from '@/components/TextField/TextField';

export interface ConfirmDialogProps {
  title: string;
  /** What happens, in plain words, and whether it can be undone. */
  children: ReactNode;
  confirmLabel: string;
  /** `danger` for anything that removes or hides something from clients. */
  tone?: 'danger' | 'primary';
  busy?: boolean;
  error?: string | null;
  /** When set, the confirm stays disabled until this exact text is typed. */
  typeToConfirm?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/** One confirmation, the same shape for every action that is hard to take back. */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  tone = 'danger',
  busy = false,
  error = null,
  typeToConfirm,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const matches = !typeToConfirm || typed.trim() === typeToConfirm.trim();

  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={!matches}
            loading={busy}
            onClick={onConfirm}
            variant={tone === 'danger' ? 'danger' : 'primary'}
          >
            {confirmLabel}
          </Button>
        </>
      }
      dismissable={!busy}
      onClose={onCancel}
      title={title}
    >
      <div>{children}</div>
      {typeToConfirm ? (
        <TextField
          autoComplete="off"
          label={`Type “${typeToConfirm}” to confirm`}
          onChange={(event) => setTyped(event.target.value)}
          value={typed}
        />
      ) : null}
      {error ? (
        <Callout tone="danger" title="That didn't work">
          {error}
        </Callout>
      ) : null}
    </Dialog>
  );
}
